import React, { useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Upload, AlertTriangle, Loader2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useBackupRestore } from '@/hooks/useBackupRestore';
import { useDataManagement } from '@/hooks/useDataManagement';

const BackupSettings = () => {
  const { forceReloadData } = useDataManagement();
  const { isExporting, isImporting, handleExportBackup, handleImportBackup } = useBackupRestore(forceReloadData);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setIsConfirmOpen(true);
    }
    // Reseta o input para permitir selecionar o mesmo arquivo novamente se cancelar
    e.target.value = null;
  };

  const confirmImport = async () => {
    if (selectedFile) {
      await handleImportBackup(selectedFile);
      setSelectedFile(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-slate-900 border-white/10">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-400" /> Exportar Dados (Backup)
          </CardTitle>
          <CardDescription>
            Baixe um arquivo seguro contendo todas as suas vendas, recebimentos, operadoras, bandeiras e configurações desta conta.
            Guarde este arquivo em um local seguro.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={handleExportBackup} 
            disabled={isExporting || isImporting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isExporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando Backup...</> : <><Download className="w-4 h-4 mr-2" /> Baixar Backup Completo</>}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-white/10">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <Upload className="w-5 h-5 text-orange-400" /> Restaurar Dados (Importar)
          </CardTitle>
          <CardDescription>
            Restaure todos os seus dados a partir de um arquivo de backup (.bkp) gerado anteriormente.
            <strong className="text-red-400 block mt-2">Atenção: A restauração apagará todos os dados atuais desta conta antes de importar os dados do backup.</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={onFileChange} 
            accept=".bkp,.json" 
            className="hidden" 
          />
          <Button 
            onClick={() => fileInputRef.current?.click()} 
            disabled={isExporting || isImporting}
            variant="destructive"
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {isImporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restaurando Dados...</> : <><Upload className="w-4 h-4 mr-2" /> Importar e Restaurar Backup</>}
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent className="bg-slate-900 border border-red-500/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" /> Confirmação de Restauração Crítica
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Você selecionou o arquivo <strong>{selectedFile?.name}</strong>.
              <br/><br/>
              Ao prosseguir, <strong>TODAS AS VENDAS, RECEBIMENTOS E OPERADORAS</strong> atualmente nesta conta serão <strong>APAGADOS</strong> permanentemente e substituídos pelo conteúdo deste backup.
              <br/><br/>
              A conciliação será rodada automaticamente após a importação. Tem certeza absoluta de que deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 text-white hover:bg-slate-700 border-white/10">Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmImport}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Sim, Apagar e Restaurar Tudo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupSettings;
